// Event/Party code sharing helpers.
//
// An "event" lets a host share one address behind a short, human-readable
// code that friends type in to reveal the address. Codes live in a top-level
// `events/{CODE}` collection (doc id IS the code) so joiners - who are not the
// host and can't read the host's private `users/{uid}` data - can look them up
// by code alone. Knowing the code is the capability; that's why Firestore
// rules let any signed-in user read an event doc (see firestore.rules).
//
// Events auto-expire via a Firestore TTL policy on the `expiresAt` field
// (configured once in the Firebase console - see BACKLOG.md), so there's no
// cleanup code here. The host chooses how long the address stays visible at
// creation (default 24h, up to a 30-day hard cap) - auto-expiry is a privacy
// feature (a leaked code can't expose the location forever), which is why even
// "Until I end it" is capped and the host can always end early.
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../Configuration/firebase";

// Ambiguous characters (0/O, 1/I/L) are removed so a code read aloud or off a
// screen can't be mistyped.
const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const CODE_LENGTH = 6;
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
export const EVENT_TTL_MS = 24 * HOUR_MS; // default visibility window
export const DEFAULT_TTL_MS = EVENT_TTL_MS;
// Hard cap on how long an address can stay visible - even "Until I end it" can't
// live forever, so a leaked code eventually stops resolving on its own.
export const MAX_TTL_MS = 30 * DAY_MS;

// How long the shared address stays visible, offered at event creation. `ms` is
// what we store as the window; "Until I end it" uses the cap and relies on the
// host ending it (or the cap) to stop it.
export const VISIBILITY_OPTIONS = [
  { id: "24h", label: "24 hours", hint: "A one-off meetup", ms: 24 * HOUR_MS },
  { id: "3d", label: "3 days", hint: "A weekend or trip", ms: 3 * DAY_MS },
  { id: "7d", label: "1 week", hint: "Plan well ahead", ms: 7 * DAY_MS },
  {
    id: "until",
    label: "Until I end it",
    hint: "Ends when you do (max 30 days)",
    ms: MAX_TTL_MS,
  },
];

// Public shareable link for an event. Opens a no-login web page (served by the
// eventPage Cloud Function via a Hosting rewrite) that shows the address and
// prompts non-users to install the app - see functions/index.js.
export const eventShareUrl = (code) => `https://iblink.app/e/${code}`;

function randomCode() {
  let code = "";
  const values = new Uint32Array(CODE_LENGTH);
  // crypto is available in both the browser and the Capacitor WebView.
  crypto.getRandomValues(values);
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += CODE_ALPHABET[values[i] % CODE_ALPHABET.length];
  }
  return code;
}

// Creates an event for `address`, retrying on the (astronomically unlikely)
// event of a code collision. Returns the generated code.
export async function createEvent({
  host,
  name,
  addressTitle,
  address,
  ttlMs = DEFAULT_TTL_MS,
}) {
  const now = Date.now();
  // Clamp to a sane range: at least a minute, never past the hard cap.
  const windowMs = Math.min(
    Math.max(Number(ttlMs) || DEFAULT_TTL_MS, 60 * 1000),
    MAX_TTL_MS,
  );
  // The host's IANA timezone (e.g. "America/Los_Angeles"), stored so the admin
  // dashboard can show event times as the host experienced them.
  let hostTimeZone = "";
  try {
    hostTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    hostTimeZone = "";
  }
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = randomCode();
    const eventRef = doc(db, "events", code);
    const existing = await getDoc(eventRef);
    if (existing.exists()) continue; // collision - try another code

    const hostName =
      [host.firstName, host.lastName].filter(Boolean).join(" ").trim() ||
      host.email ||
      "A friend";

    await setDoc(eventRef, {
      hostUid: host.uid,
      hostName,
      hostTimeZone,
      // Optional custom event name (e.g. "Sarah's Birthday"). Blank falls back
      // to hostName wherever the event is titled - see EventDetails.
      name: (name || "").trim(),
      addressTitle: addressTitle || "",
      address,
      createdAt: serverTimestamp(),
      // Client-computed so we have a concrete Timestamp for the TTL policy and
      // the countdown. serverTimestamp() can't be read back synchronously.
      expiresAt: Timestamp.fromMillis(now + windowMs),
      active: true,
    });
    return code;
  }
  throw new Error("Could not generate a unique event code. Please try again.");
}

export function normalizeCode(raw) {
  return (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

// Loads an event by code and validates it's joinable. Throws a user-facing
// Error otherwise so callers can surface `error.message` directly.
export async function fetchJoinableEvent(rawCode) {
  const code = normalizeCode(rawCode);
  if (code.length !== CODE_LENGTH) {
    throw new Error(`Codes are ${CODE_LENGTH} characters. Check and try again.`);
  }
  const snap = await getDoc(doc(db, "events", code));
  if (!snap.exists()) {
    throw new Error("No event found for that code.");
  }
  const data = snap.data();
  if (data.active === false) {
    throw new Error("This event has ended.");
  }
  if (data.expiresAt && data.expiresAt.toMillis() < Date.now()) {
    throw new Error("This event code has expired.");
  }
  return { code, ...data };
}

// Records the current user as a participant. pid == uid so a user occupies one
// participant slot no matter how many times they join, and matches the
// firestore.rules constraint that a joiner may only write their own doc.
export async function joinEvent({ code, user, name }) {
  const participantRef = doc(db, "events", code, "participants", user.uid);
  await setDoc(
    participantRef,
    {
      uid: user.uid,
      name: name || "Guest",
      joinedAt: serverTimestamp(),
    },
    { merge: true },
  );
  // Also record the code on the guest's own user doc so getMyJoinedEvents can
  // list it on any device (see recordJoinedEvent).
  await recordJoinedEvent(user.uid, code);
}

// Records that `uid` joined `code` on their user doc
// (users/{uid}.joinedEventCodes[]). That list is how getMyJoinedEvents finds a
// guest's events on every device - built on getDoc + a user-doc read, which
// work reliably in the iOS WebView, unlike a collectionGroup query. Called on
// join and when a guest opens an event (to backfill events joined earlier).
// Best-effort: the user owns their doc, so the write is allowed by rules.
export async function recordJoinedEvent(uid, code) {
  try {
    await setDoc(
      doc(db, "users", uid),
      { joinedEventCodes: arrayUnion(code) },
      { merge: true },
    );
  } catch (err) {
    console.error("Failed to record joined event:", err);
  }
}

export async function endEvent(code) {
  await setDoc(
    doc(db, "events", code),
    { active: false, endedAt: serverTimestamp() },
    { merge: true },
  );
}

// Host keeps a running event visible for a further `ms` from now (re-arming the
// countdown), capped at the 30-day max. Guests on the live screen pick the new
// expiry up via subscribeEvent. Rules already allow the host to update.
export async function extendEvent(code, ms) {
  const windowMs = Math.min(Number(ms) || DEFAULT_TTL_MS, MAX_TTL_MS);
  await setDoc(
    doc(db, "events", code),
    { expiresAt: Timestamp.fromMillis(Date.now() + windowMs) },
    { merge: true },
  );
}

// Host renames the event after creation. Blank clears the custom name, so the
// title falls back to the host's name again. Guests on the live screen pick the
// change up through subscribeEvent. Firestore rules already allow the host to
// update their own event doc, so no rule change is needed for this.
export async function updateEventName(code, name) {
  await setDoc(
    doc(db, "events", code),
    { name: (name || "").trim() },
    { merge: true },
  );
}

// Host changes the shared address after creation. Guests on the live event
// screen pick the new address up through subscribeEvent - no notification is
// sent. Firestore rules already allow the host to update their own event doc.
export async function updateEventAddress(code, { addressTitle, address }) {
  await setDoc(
    doc(db, "events", code),
    { addressTitle: addressTitle || "", address },
    { merge: true },
  );
}

// hostName is stored (denormalized) on each event because guests can't read the
// host's private user doc. So when the host renames their profile, push the new
// name onto all their active events - otherwise guests keep seeing the old
// "Hosted by <name>". Guests viewing live update via subscribeEvent. Best-effort.
export async function refreshHostNameOnEvents(uid, hostName) {
  try {
    const events = await getMyHostedEvents(uid);
    await Promise.all(
      events.map((e) =>
        setDoc(doc(db, "events", e.code), { hostName }, { merge: true }),
      ),
    );
  } catch (err) {
    console.error("Failed to refresh host name on events:", err);
  }
}

// A guest leaves an event by removing their own participant doc (rules allow
// deleting only your own, where pid == uid) and dropping the code from their
// joined list so it stops showing on every device.
export async function leaveEvent({ code, uid }) {
  await deleteDoc(doc(db, "events", code, "participants", uid));
  await setDoc(
    doc(db, "users", uid),
    { joinedEventCodes: arrayRemove(code) },
    { merge: true },
  );
}

// All of a host's still-active, unexpired events, straight from Firestore (the
// source of truth). Secure: the query filters hostUid == uid, which is exactly
// what the events `list` rule requires, so a host can only ever list their own.
export async function getMyHostedEvents(uid) {
  const q = query(collection(db, "events"), where("hostUid", "==", uid));
  const snap = await getDocs(q);
  const now = Date.now();
  return snap.docs
    .map((d) => ({ code: d.id, ...d.data() }))
    .filter(
      (e) =>
        e.active !== false && (!e.expiresAt || e.expiresAt.toMillis() > now),
    )
    .sort(
      (a, b) =>
        (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0),
    );
}

// All events the current user has JOINED as a guest, from Firestore so the list
// is identical on every device. Reads the codes off the user's own doc
// (joinedEventCodes[]) and loads each event with getDoc - deliberately NO
// collectionGroup query, which silently fails in the iOS WebView (only hosted
// events, a normal query, worked there). Ended/expired events are dropped and
// their codes pruned so the list stays tidy.
export async function getMyJoinedEvents(uid) {
  const userSnap = await getDoc(doc(db, "users", uid));
  const codes =
    userSnap.exists() && Array.isArray(userSnap.data().joinedEventCodes)
      ? userSnap.data().joinedEventCodes
      : [];
  if (codes.length === 0) return [];

  const now = Date.now();
  const results = await Promise.all(
    codes.map(async (code) => {
      const evSnap = await getDoc(doc(db, "events", code));
      if (!evSnap.exists()) return { code, dead: true };
      const data = evSnap.data();
      if (data.active === false) return { code, dead: true };
      if (data.expiresAt && data.expiresAt.toMillis() < now) {
        return { code, dead: true };
      }
      return { code, data };
    }),
  );

  // Prune codes for events that are gone/ended (best-effort).
  const dead = results.filter((r) => r.dead).map((r) => r.code);
  if (dead.length > 0) {
    setDoc(
      doc(db, "users", uid),
      { joinedEventCodes: arrayRemove(...dead) },
      { merge: true },
    ).catch((err) => console.error("Failed to prune joined codes:", err));
  }

  return results
    .filter((r) => !r.dead)
    .map((r) => ({ code: r.code, ...r.data }))
    .sort(
      (a, b) =>
        (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0),
    );
}

// Live subscription to an event's participant list. Returns the unsubscribe fn.
export function subscribeParticipants(code, callback) {
  const q = query(
    collection(db, "events", code, "participants"),
    orderBy("joinedAt", "asc"),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// Live subscription to the event doc itself (so host + guests see it end).
export function subscribeEvent(code, callback) {
  return onSnapshot(doc(db, "events", code), (snap) => {
    callback(snap.exists() ? { code, ...snap.data() } : null);
  });
}
